/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "CLI overview", description = "Hightouch provides an intuitive Command Line Interface (CLI) to give users access to resources in the terminal or automation scripts.", lastUpdatedDate = new Date(1679011200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "CLI overview",
        description: "Hightouch provides an intuitive Command Line Interface (CLI) to give users access to resources in the terminal or automation scripts.",
        lastUpdatedDate: "Fri Mar 17 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Quick start",
          "depth": 0,
          "id": "quick-start"
        }, {
          "value": "Install CLI",
          "depth": 1,
          "id": "install-cli"
        }, {
          "value": "Login with API key",
          "depth": 1,
          "id": "login-with-api-key"
        }, {
          "value": "CLI reference",
          "depth": 0,
          "id": "cli-reference"
        }, {
          "value": "Issues",
          "depth": 0,
          "id": "issues"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      h3: "h3",
      pre: "pre",
      code: "code",
      span: "span",
      a: "a"
    }, _provideComponents(), props.components), {CreateAPIKeySnippet, PylonLink} = _components;
    if (!CreateAPIKeySnippet) _missingMdxReference("CreateAPIKeySnippet", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch provides an intuitive Command Line Interface (CLI) to give users access to resources in the terminal or automation scripts.\nYou can use the CLI to access the definition of your resources like syncs and models or trigger a sync run from your terminal or automation scripts."
      }), "\n", _jsx(_components.h2, {
        id: "quick-start",
        children: "Quick start"
      }), "\n", _jsx(_components.h3, {
        id: "install-cli",
        children: "Install CLI"
      }), "\n", _jsx(_components.p, {
        children: "To install from shell scripts, run:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-shell",
          children: [_jsx(_components.span, {
            className: "hljs-meta prompt_",
            children: "# "
          }), _jsx(_components.span, {
            className: "bash",
            children: "Install the latest version"
          }), "\n", _jsx(_components.span, {
            className: "hljs-meta prompt_",
            children: "$ "
          }), _jsx(_components.span, {
            className: "bash",
            children: "curl -sLf https://getcli.hightouch.io | sh -"
          }), "\n", _jsx(_components.span, {
            className: "hljs-meta prompt_",
            children: "\n# "
          }), _jsx(_components.span, {
            className: "bash",
            children: "Install specific version"
          }), "\n", _jsx(_components.span, {
            className: "hljs-meta prompt_",
            children: "$ "
          }), _jsx(_components.span, {
            className: "bash",
            children: "curl -sLf https://getcli.hightouch.io | INSTALL_HT_VERSION=v0.1.0 sh -"
          }), "\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "login-with-api-key",
        children: "Login with API key"
      }), "\n", _jsx(_components.p, {
        children: "Next, you need to create a Hightouch API key in your Hightouch workspace settings."
      }), "\n", _jsx(CreateAPIKeySnippet, {}), "\n", _jsx(_components.p, {
        children: "Then you can login with this API key:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-shell",
          children: [_jsx(_components.span, {
            className: "hljs-meta prompt_",
            children: "$ "
          }), _jsx(_components.span, {
            className: "bash",
            children: "ht login"
          }), "\nHightouch API key [************************************]:\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "You can also use your API key with ENV variables."
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-shell",
          children: "HIGHTOUCH_APIKEY=${yourkey} ht COMMANDS\n"
        })
      }), "\n", _jsx(_components.h2, {
        id: "cli-reference",
        children: "CLI reference"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've installed and logged in, you can follow the ", _jsx(_components.a, {
          href: "/developer-tools/cli-reference",
          children: "CLI Reference documentation"
        }), " to start using the CLI."]
      }), "\n", _jsx(_components.h2, {
        id: "issues",
        children: "Issues"
      }), "\n", _jsxs(_components.p, {
        children: ["If you have any problems using CLI, please file an issue through ", _jsx(_components.a, {
          href: "https://github.com/hightouchio/cli/issues",
          children: "GitHub"
        }), ", or contact us through Slack or", _jsx(PylonLink, {
          pylonText: "Hello, I need some help using the CLI.",
          linkText: "Intercom"
        }), "."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
